import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, X } from "lucide-react";
import {
  getDepartments, createDepartment, updateDepartment, deleteDepartment,
} from "../../api/api";
import DataTable from "../../components/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

const EMPTY = { name: "", description: "", active: true };

function DepartmentForm({ open, onOpenChange, department, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!department;

  useEffect(() => {
    if (!open) return;
    setForm(department ? { ...EMPTY, ...department } : EMPTY);
  }, [open, department]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        active: !!form.active,
      };
      if (editing) await updateDepartment(department._id, payload);
      else await createDepartment(payload);
      toast.success(editing ? "Department updated." : "Department added.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save the department.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit department" : "Add department"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="dept-name">Name *</Label>
            <Input id="dept-name" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Cardiology" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dept-desc">Description (optional)</Label>
            <textarea
              id="dept-desc"
              className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Heart and cardiovascular conditions."
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v)} />
            Active — available to assign doctors to
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add department"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DepartmentMaster() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getDepartments({ search: search || undefined, page, limit: 20 })
      .then((res) => {
        setDepartments(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load departments."))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (department) => {
    try {
      const res = await deleteDepartment(department._id);
      toast.success(res.message || "Department removed.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not remove the department.");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Department",
      render: (d) => (
        <div className="min-w-0">
          <p className="truncate font-medium leading-tight">{d.name}</p>
          {d.description && <p className="truncate text-xs leading-tight text-muted-foreground">{d.description}</p>}
        </div>
      ),
    },
    {
      key: "doctor_count",
      header: "Doctors",
      render: (d) => <span className="tabular-nums text-sm">{d.doctor_count}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (d) => (
        <Badge variant={d.active ? "secondary" : "outline"}>
          {d.active ? "Active" : "Retired"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (d) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8"
            aria-label={`Edit ${d.name}`}
            onClick={() => { setEditing(d); setFormOpen(true); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${d.name}`}
            onClick={() => remove(d)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Departments</h2>
          <p className="text-sm text-muted-foreground">
            The clinical departments doctors are organised under
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4" /> Add department
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name…"
          className="h-9 pl-9 pr-9"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {!loading && (
        <p className="text-sm text-muted-foreground">
          {meta.total} department{meta.total === 1 ? "" : "s"}
        </p>
      )}

      <DataTable
        columns={columns}
        data={departments}
        loading={loading}
        emptyMessage={search ? "No departments match that search." : "No departments yet."}
        emptyAction={
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" /> Add the first department
          </Button>
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(d) => d._id}
      />

      <DepartmentForm
        open={formOpen}
        onOpenChange={setFormOpen}
        department={editing}
        onSaved={load}
      />
    </div>
  );
}
